import type { MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import posthog from 'posthog-js';
import { MapObject } from '../../models/mapObject';
import type { MarketplaceListing } from '../../models/apiTypes';
import type { UserData } from '../../models/user';
import { mapSceneSingleton } from './mapScene';
import type { HighlightManager } from './controller/HighlightManager';
import type { MapModalName, MapModalPayload } from '../../stores/useMapModalStore';
import type { useCreateUserAction } from '../../hooks/useCreateUserAction';

// ── Marker click-handling cluster (plot / map-object) ──
// Extracted verbatim from MapCanvas (map-architecture Phase 8). Bodies are
// byte-identical to the originals except that every closure dependency (scene
// refs initialised from mapSceneSingleton, React state/store setters, and the
// helper functions that stay in MapCanvas because they're shared by code
// outside this cluster) is received as a parameter. `setupClickHandlers` and
// `ClickStartData` were used ONLY by this cluster, so they moved here too.

export interface ClickStartData {
  x: number;
  y: number;
  mapObject: MapObject | null;
  marker: PIXI.Container | null;
  worldX: number;
  worldY: number;
  hasMoved: boolean;
}

export interface UseMapClickHandlersParams {
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  highlightManagerRef: MutableRefObject<HighlightManager | null>;
  // Callbacks that remain in MapCanvas (used both inside and outside this cluster)
  setupAdminDragAndDrop: (mapObject: MapObject, marker: PIXI.Container, event: PIXI.FederatedPointerEvent) => void;
  getLatestMapObject: (mapObject: MapObject | null | undefined) => MapObject | null;
  hideQuestMarker: (mapObject: MapObject) => void;
  getMoveThreshold: (event: PIXI.FederatedPointerEvent) => number;
  setSelectedMapObject: (mapObject: MapObject | null) => void;
  setLighthouseObject: (mapObject: MapObject | null) => void;
  createUserAction: ReturnType<typeof useCreateUserAction>['createUserAction'];
  openModal: (modal: MapModalName, payload?: Partial<MapModalPayload>) => void;
  // Data owned by MapCanvas
  listingsByHouseId: Map<number, MarketplaceListing>;
  userData: UserData | null | undefined;
  // Constants owned by MapCanvas (also consumed outside this cluster)
  STATIC_OBJECT_FIXED_SIZES: Record<string, { width: number; height: number }>;
}

export interface UseMapClickHandlersResult {
  handleMapObjectClick: (mapObject: MapObject, marker: PIXI.Container, event: PIXI.FederatedPointerEvent) => void;
  handlePlotClick: (mapObject: MapObject, marker: PIXI.Container, event: PIXI.FederatedPointerEvent) => void;
}

export function useMapClickHandlers(params: UseMapClickHandlersParams): UseMapClickHandlersResult {
  const {
    sceneRef,
    highlightManagerRef,
    setupAdminDragAndDrop,
    getLatestMapObject,
    hideQuestMarker,
    getMoveThreshold,
    setSelectedMapObject,
    setLighthouseObject,
    createUserAction,
    openModal,
    listingsByHouseId,
    userData,
    STATIC_OBJECT_FIXED_SIZES,
  } = params;

  const setupClickHandlers = (
    marker: PIXI.Container,
    onValidClick: () => void
  ) => {
    let viewportMoveHandler: ((moveEvent: PIXI.FederatedPointerEvent) => void) | null = null;

    const handlePointerMove = (moveEvent: PIXI.FederatedPointerEvent) => {
      if (!sceneRef.current.admin.clickStart || !sceneRef.current.pixi.viewport) return;

      const moveX = moveEvent.global.x;
      const moveY = moveEvent.global.y;
      const moveDistance = Math.sqrt(
        Math.pow(moveX - sceneRef.current.admin.clickStart.x, 2) +
        Math.pow(moveY - sceneRef.current.admin.clickStart.y, 2)
      );

      if (moveDistance > getMoveThreshold(moveEvent)) {
        sceneRef.current.admin.clickStart.hasMoved = true;
      }
    };

    if (sceneRef.current.pixi.viewport) {
      viewportMoveHandler = (moveEvent: PIXI.FederatedPointerEvent) => {
        if (!sceneRef.current.admin.clickStart) return;

        const moveX = moveEvent.global.x;
        const moveY = moveEvent.global.y;
        const moveDistance = Math.sqrt(
          Math.pow(moveX - sceneRef.current.admin.clickStart.x, 2) +
          Math.pow(moveY - sceneRef.current.admin.clickStart.y, 2)
        );

        if (moveDistance > getMoveThreshold(moveEvent)) {
          sceneRef.current.admin.clickStart.hasMoved = true;
        }
      };

      sceneRef.current.pixi.viewport.on('pointermove', viewportMoveHandler);
    }

    const handlePointerCancel = () => {
      cleanup();
    };

    const cleanup = () => {
      sceneRef.current.admin.clickStart = null;
      marker.off('pointermove', handlePointerMove);
      marker.off('pointerup', handlePointerUp);
      marker.off('pointerupoutside', handlePointerUpOutside);
      marker.off('pointercancel', handlePointerCancel);
      if (sceneRef.current.pixi.viewport && viewportMoveHandler) {
        sceneRef.current.pixi.viewport.off('pointermove', viewportMoveHandler);
      }
    };

    const handlePointerUp = () => {
      if (!sceneRef.current.admin.clickStart || !sceneRef.current.pixi.viewport) {
        cleanup();
        return;
      }

      if (!sceneRef.current.admin.clickStart.hasMoved) {
        onValidClick();
      }

      cleanup();
    };

    const handlePointerUpOutside = () => {
      if (!sceneRef.current.admin.clickStart || !sceneRef.current.pixi.viewport) {
        cleanup();
        return;
      }

      if (!sceneRef.current.admin.clickStart.hasMoved) {
        onValidClick();
      }

      cleanup();
    };

    marker.on('pointermove', handlePointerMove);
    marker.once('pointerup', handlePointerUp);
    marker.once('pointerupoutside', handlePointerUpOutside);
    marker.once('pointercancel', handlePointerCancel);
  };

  const handleMapObjectClick = (mapObject: MapObject, marker: PIXI.Container, event: PIXI.FederatedPointerEvent) => {
    if (sceneRef.current.flags.godModeActive) {
      setupAdminDragAndDrop(mapObject, marker, event);
      return;
    }

    if (!sceneRef.current.pixi.viewport) return;

    const screenPoint = new PIXI.Point(event.global.x, event.global.y);
    const worldPos = sceneRef.current.pixi.viewport.toWorld(screenPoint);

    const containerX = marker.x;
    const containerY = marker.y;

    const sprite = marker.children[0] as PIXI.Sprite;
    let spriteWidth = 0;
    let spriteHeight = 0;
    let anchorX = 0.5;
    let anchorY = 0.5;

    if (sprite && sprite.texture) {
      if (mapObject.type === 'cityhall' || mapObject.type === 'shop' || mapObject.type === 'hell_of_flame') {
        const fixedSize = STATIC_OBJECT_FIXED_SIZES[mapObject.type];
        if (fixedSize) {
          spriteWidth = fixedSize.width;
          spriteHeight = fixedSize.height;
        }
        anchorX = 0.5;
        anchorY = 0.5;
      } else if (mapObject.type === 'house') {
        // Використовуємо фактичний розмір спрайта
        spriteWidth = sprite.width || 0;
        spriteHeight = sprite.height || 0;
        anchorX = sprite.anchor?.x ?? 0.5;
        anchorY = sprite.anchor?.y ?? 0.5;
      } else if (mapObject.type.startsWith('decor_')) {
        // Для decor об'єктів використовуємо фактичний розмір спрайта
        spriteWidth = sprite.width || 0;
        spriteHeight = sprite.height || 0;
        anchorX = sprite.anchor?.x ?? 0.5;
        anchorY = sprite.anchor?.y ?? 0.5;
      }
    }

    void spriteWidth;
    void anchorX;
    void containerX;
    void containerY;
    void spriteHeight;
    void anchorY;

    sceneRef.current.admin.clickStart = {
      x: event.global.x,
      y: event.global.y,
      mapObject,
      marker,
      worldX: worldPos.x,
      worldY: worldPos.y,
      hasMoved: false
    };

    setupClickHandlers(
      marker,
      () => {
        if (sceneRef.current.sprites.mapObjectHighlight && sceneRef.current.layers.highlight) {
          sceneRef.current.layers.highlight.removeChild(sceneRef.current.sprites.mapObjectHighlight);
          sceneRef.current.sprites.mapObjectHighlight.destroy();
          sceneRef.current.sprites.mapObjectHighlight = null;
          mapSceneSingleton.mapObjectHighlight = null;
        }

        const latestMapObject = getLatestMapObject(mapObject);
        if (latestMapObject) {
          highlightManagerRef.current!.apply(latestMapObject, marker);
          posthog.capture('map_object_visited', {
            object_type: latestMapObject.type,
            object_id: latestMapObject.id,
          });
        }

        hideQuestMarker(latestMapObject ?? mapObject);

        setSelectedMapObject(latestMapObject ?? mapObject);
        setTimeout(() => {
          const finalMapObject = latestMapObject ?? mapObject;
          if (!finalMapObject) return;
          if (finalMapObject.type === 'cityhall') {
            openModal('mapObject');
            createUserAction('visit_city_hall').catch((error) => {
              console.error("Failed to create city hall action:", error);
            });
          } else if (finalMapObject.type === 'shop') {
            openModal('navConfirm', { navConfirmDestination: 'shop' });
          } else if (finalMapObject.type === 'hell_of_flame') {
            openModal('navConfirm', { navConfirmDestination: 'hall_of_fame' });
          } else if (finalMapObject.type === 'decor_monument_cat_winston_churchill') {
            openModal('winstonCat');
          } else if (finalMapObject.type === 'decor_lighthouse') {
            setLighthouseObject(finalMapObject);
            openModal('lighthouse');
          }
        }, 100);
      }
    );
  };

  const handlePlotClick = (mapObject: MapObject, marker: PIXI.Container, event: PIXI.FederatedPointerEvent) => {
    if (sceneRef.current.flags.godModeActive) {
      setupAdminDragAndDrop(mapObject, marker, event);
      return;
    }
    if (!sceneRef.current.pixi.viewport) return;

    const screenPoint = new PIXI.Point(event.global.x, event.global.y);
    const worldPos = sceneRef.current.pixi.viewport.toWorld(screenPoint);

    const containerX = marker.x;
    const containerY = marker.y;

    const sprite = marker.children[0] as PIXI.Sprite;
    let spriteWidth = 0;
    let spriteHeight = 0;
    let anchorX = 0.5;
    let anchorY = 1.0;

    if (sprite && sprite.texture) {
      if (mapObject.type === 'house') {
        // Використовуємо фактичний розмір спрайта
        spriteWidth = sprite.width || 0;
        spriteHeight = sprite.height || 0;
        anchorX = sprite.anchor?.x ?? 0.5;
        anchorY = sprite.anchor?.y ?? 0.5;
      } else if (mapObject.type === 'plot') {
        spriteWidth = 43.651;
        spriteHeight = 55;
        anchorX = 0.5;
        anchorY = 1.0;
      }
    }

    void spriteWidth;
    void anchorX;
    void containerX;
    void containerY;
    void spriteHeight;
    void anchorY;

    sceneRef.current.admin.clickStart = {
      x: event.global.x,
      y: event.global.y,
      mapObject,
      marker,
      worldX: worldPos.x,
      worldY: worldPos.y,
      hasMoved: false
    };

    setupClickHandlers(
      marker,
      () => {
        if (sceneRef.current.sprites.mapObjectHighlight && sceneRef.current.layers.highlight) {
          sceneRef.current.layers.highlight.removeChild(sceneRef.current.sprites.mapObjectHighlight);
          sceneRef.current.sprites.mapObjectHighlight.destroy();
          sceneRef.current.sprites.mapObjectHighlight = null;
          mapSceneSingleton.mapObjectHighlight = null;
        }

        if (mapObject) {
          highlightManagerRef.current!.apply(mapObject, marker);
          posthog.capture('map_object_visited', {
            object_type: mapObject.type,
            object_id: mapObject.id,
          });
        }

        const latestMapObject = getLatestMapObject(mapObject);
        const pickedObject = latestMapObject ?? mapObject;
        setSelectedMapObject(pickedObject);
        setTimeout(() => {
          const finalMapObject = latestMapObject ?? mapObject;
          if (!finalMapObject) {
            return;
          }
          if (finalMapObject.type === 'house') {
            if (finalMapObject.owner_id && userData?.user_id && String(finalMapObject.owner_id) !== userData.user_id && finalMapObject.type === 'house') {
              createUserAction('visit_player_house', finalMapObject.id).catch((error) => {
                console.error("Failed to create visit_player_house action:", error);
              });
            }
            // Show intermediate sale preview modal for listed houses the user doesn't own
            const listing = listingsByHouseId.get(finalMapObject.id);
            if (listing && finalMapObject.owner_id && userData?.user_id && String(finalMapObject.owner_id) !== userData.user_id) {
              openModal('salePreview', { salePreviewListing: listing });
            } else {
              openModal('realEstateInfo');
            }
          } else if (finalMapObject.type === 'plot') {
            if (finalMapObject.owner_id === null) {
              posthog.capture('free_plot_opened', { map_object_id: finalMapObject.id });
              openModal('plotFree', { realEstateCatalogPlotId: finalMapObject.id });
            } else {
              openModal('realEstate', { realEstateCatalogPlotId: finalMapObject.id });
            }
          } else if (finalMapObject.type === 'decor_monument_cat_winston_churchill') {
            openModal('winstonCat');
          } else if (finalMapObject.type === 'decor_lighthouse') {
            setLighthouseObject(finalMapObject);
            openModal('lighthouse');
          }
        }, 100);
      }
    );
  };

  return { handleMapObjectClick, handlePlotClick };
}
